import React, { useState } from "react";
import { trackQuote } from "@/lib/track";
import { scrollToId } from "@/lib/scrollToId";
import {
  ClipboardCheck,
  FileText,
  Layers,
  Droplets,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

interface ProcessStep {
  num: string;
  title: string;
  icon: React.ElementType;
  description: string;
  details: string[];
}

const steps: ProcessStep[] = [
  {
    num: "01",
    title: "Site Visit or Send Us Your Details",
    icon: ClipboardCheck,
    description:
      "Two ways to get started. We can come to your site to measure and assess everything in person, or if you prefer, simply send us your details and we can give you a quote online or over the phone.",
    details: [
      "Option 1: Book a free site visit and we'll measure and assess everything",
      "Option 2: Send us your measurements and we'll quote by phone or online",
      "Measurements of the area (square metres)",
      "Depth of screed needed (millimetres)",
      "Access for the screed delivery truck",
      "Your full address and contact details",
    ],
  },
  {
    num: "02",
    title: "Clear, Fixed Price",
    icon: FileText,
    description:
      "You get a straightforward, fixed-price quote with no hidden costs. We tell you exactly which screed we'll use, how deep it'll be, and how long it'll take, so you know precisely what you're paying for.",
    details: [
      "A clear itemised quote, no surprises",
      "The exact screed type and depth we'll use",
      "How long until you can walk on it and fit your floor",
      "Your 10-year Cemfloor product guarantee confirmed",
    ],
  },
  {
    num: "03",
    title: "Getting the Floor Ready",
    icon: Layers,
    description:
      "We lay down a protective sheet, fit edge insulation around the walls, and set up laser guides to make sure the finished floor is perfectly flat across the whole area.",
    details: [
      "Protective sheeting laid down to stop damp",
      "Edge insulation fitted around the walls",
      "Laser guides set up for a perfectly flat finish",
      "Underfloor heating pipes pressure-tested",
    ],
  },
  {
    num: "04",
    title: "Pouring & Finishing",
    icon: Droplets,
    description:
      "We pump the liquid screed straight to the pour area, level it with laser guides, and smooth the surface. Your floor will be ready to walk on within 24 to 48 hours.",
    details: [
      "We can pump up to 1,000m² in a single day",
      "Laser-guided levelling for a perfectly flat surface",
      "Smooth finish ready for your final flooring",
      "Walk on it within 24 to 48 hours of pouring",
    ],
  },
];

export const PourProcess: React.FC = () => {
  const [active, setActive] = useState(0);
  const ActiveIcon = steps[active].icon;

  return (
    <section
      id="process"
      className="py-20 bg-[#FAF8F5] text-stone-900 border-b border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-xs sm:text-sm uppercase tracking-widest text-[#B38D30] font-bold block mb-2">
            HOW WE WORK
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-stone-900 tracking-tight mb-4">
            From Start to{" "}
            <span className="gold-gradient-text">Finished Floor</span>
          </h2>
          <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
            Our straightforward four-step process guarantees a perfectly level
            surface. Here is how we deliver your project.
          </p>
        </div>

        {/* Desktop: Timeline + Detail */}
        <div className="hidden lg:grid grid-cols-12 gap-10">
          {/* Timeline column */}
          <div className="col-span-5">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-7 top-2 bottom-2 w-0.5 bg-stone-200" />

              <div className="space-y-1">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  const isActive = i === active;
                  return (
                    <button
                      key={s.num}
                      onClick={() => setActive(i)}
                      className="relative flex items-start gap-4 w-full text-left p-3 rounded-xl transition-colors group"
                    >
                      <div
                        className={`relative z-10 flex items-center justify-center w-14 h-14 rounded-full shrink-0 border-2 transition-all ${
                          isActive
                            ? "bg-[#C9A84C] border-[#C9A84C] text-stone-950 shadow-lg shadow-amber-500/20"
                            : "bg-white border-stone-300 text-stone-400 group-hover:border-[#C9A84C] group-hover:text-[#B38D30]"
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="pt-2">
                        <span
                          className={`text-xs font-bold tracking-widest block mb-0.5 ${isActive ? "text-[#B38D30]" : "text-stone-400"}`}
                        >
                          STEP {s.num}
                        </span>
                        <h3
                          className={`text-base font-bold leading-tight transition-colors ${isActive ? "text-stone-900" : "text-stone-500"}`}
                        >
                          {s.title}
                        </h3>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Detail column */}
          <div className="col-span-7">
            <div className="bg-white border border-stone-200 rounded-2xl shadow-lg p-8 sticky top-32">
              <div className="flex items-center gap-4 mb-5">
                <div className="p-3.5 rounded-xl bg-[#C9A84C]/15 text-[#B38D30]">
                  <ActiveIcon className="w-7 h-7" />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#B38D30] block">
                    Step {steps[active].num}
                  </span>
                  <h3 className="text-xl font-extrabold text-stone-900 leading-tight">
                    {steps[active].title}
                  </h3>
                </div>
              </div>

              <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-6">
                {steps[active].description}
              </p>

              <ul className="space-y-2.5 mb-6">
                {steps[active].details.map((d) => (
                  <li key={d} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0 mt-0.5" />
                    <span className="text-stone-700 text-sm leading-relaxed">
                      {d}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  trackQuote("process_step1");
                  scrollToId("#contact");
                }}
                className="inline-flex items-center gap-2 text-[#B38D30] hover:text-[#8c6b1f] font-bold text-sm tracking-wide transition-colors group"
              >
                <span>Book your site survey</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Mobile: Accordion-style */}
        <div className="lg:hidden space-y-4">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isOpen = i === active;
            return (
              <div
                key={s.num}
                className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => setActive(isOpen ? -1 : i)}
                  className="w-full flex items-center gap-4 p-4 text-left"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#C9A84C]/15 text-[#B38D30] shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-bold tracking-widest text-[#B38D30] block">
                      STEP {s.num}
                    </span>
                    <h3 className="text-base font-bold text-stone-900 leading-tight">
                      {s.title}
                    </h3>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-5">
                    <p className="text-stone-600 text-sm leading-relaxed mb-4">
                      {s.description}
                    </p>
                    <ul className="space-y-2">
                      {s.details.map((d) => (
                        <li key={d} className="flex items-start gap-2.5">
                          <CheckCircle2 className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
                          <span className="text-stone-700 text-sm leading-relaxed">
                            {d}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
